import { useState, useEffect } from 'react';
import { Spin, notification } from 'antd';
import {
  CalendarOutlined, CloseOutlined, EnvironmentOutlined, HeartOutlined,
  LinkOutlined, MessageOutlined, PhoneOutlined, TeamOutlined, TrophyOutlined,
  UserOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader } from '../shared/utils';

const fmt = v => v || '—';
const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const inrFmt = a => Number(a || 0).toLocaleString('en-IN');

export function MemberProfileModal({ memberId, onClose, onSwitchTab }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    axios.get(`${baseUrl}/member/public-profile/${memberId}`, { headers: authHeader() })
      .then(r => { if (r.data.status) setProfile(r.data.result); })
      .catch(() => notification.error({ message: 'Profile load failed', placement: 'topRight' }))
      .finally(() => setLoading(false));
  }, [memberId]);

  const sendConnect = async () => {
    if (!profile || connecting) return;
    setConnecting(true);
    try {
      await axios.post(`${baseUrl}/member/contact-request`, { to_user_id: profile.id }, { headers: authHeader() });
      notification.success({ message: '🤝 संपर्क अनुरोध भेजा गया!' });
      setProfile(p => ({ ...p, connection_status: 'sent' }));
    } catch (err) {
      notification.warning({ message: err.response?.data?.message || 'Error' });
    } finally { setConnecting(false); }
  };

  const openChat = () => {
    if (onSwitchTab) onSwitchTab('chat', profile.id);
    onClose();
  };

  const ConnectBtn = () => {
    const s = profile.connection_status;
    if (s === 'accepted') return (
      <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 8, padding: '6px 12px' }}>✅ जुड़े हैं</span>
    );
    if (s === 'sent') return (
      <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '6px 12px' }}>⏳ अनुरोध भेजा</span>
    );
    if (s === 'pending') return (
      <button onClick={async () => {
        setConnecting(true);
        try {
          await axios.patch(`${baseUrl}/member/contact-request/${profile.connection_id}`, { action: 'accept' }, { headers: authHeader() });
          notification.success({ message: '✅ संपर्क जोड़ा गया!' });
          setProfile(p => ({ ...p, connection_status: 'accepted' }));
          axios.get(`${baseUrl}/member/public-profile/${memberId}`, { headers: authHeader() })
            .then(r => { if (r.data.status) setProfile(r.data.result); }).catch(() => {});
        } catch {} finally { setConnecting(false); }
      }} disabled={connecting} style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#1d4ed8', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
        ✓ स्वीकार करें
      </button>
    );
    return (
      <button onClick={sendConnect} disabled={connecting} style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: M, border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
        <LinkOutlined /> {connecting ? '...' : 'संपर्क जोड़ें'}
      </button>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', paddingBottom: 32 }}>

        {/* Header */}
        <div style={{ position: 'sticky', top: 0, background: '#fff', borderBottom: '1px solid #f3f4f6', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>सदस्य प्रोफ़ाइल</div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CloseOutlined style={{ fontSize: 12, color: '#6b7280' }} />
          </button>
        </div>

        {loading && <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>}

        {!loading && profile && (
          <>
            {/* Hero */}
            <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, padding: '20px 16px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(255,255,255,0.5)', flexShrink: 0 }}>
                {profile.photo
                  ? <img src={profile.photo} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                  : <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800, color: G }}>{ini(profile.name)}</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{fmt(profile.name_hn)}</div>
                {profile.name_eng && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{profile.name_eng}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {profile.membership_no && <span style={{ fontSize: 10, fontWeight: 800, background: G, color: M, borderRadius: 20, padding: '2px 9px' }}>#{profile.membership_no}</span>}
                  {profile.member_status && <span style={{ fontSize: 10, background: profile.member_status === 'Active' ? '#dcfce7' : 'rgba(255,255,255,0.15)', color: profile.member_status === 'Active' ? '#15803d' : '#fff', borderRadius: 20, padding: '2px 9px' }}>{profile.member_status}</span>}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 12 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: G }}>{profile.contacts_count}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>मित्र</div>
                  </div>
                  {profile.mutual_count > 0 && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#fde68a' }}>{profile.mutual_count}</div>
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>mutual</div>
                    </div>
                  )}
                  {profile.total_donated > 0 && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: G }}>₹{inrFmt(profile.total_donated)}</div>
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>समर्पण</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ padding: '12px 16px', display: 'flex', gap: 8, borderBottom: '1px solid #f3f4f6' }}>
              <ConnectBtn />
              <button onClick={openChat} style={{ fontSize: 11, fontWeight: 700, color: M, background: '#fff5f5', border: `1px solid ${M}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <MessageOutlined /> संदेश
              </button>
              {profile.phone && (
                <>
                  <a href={`tel:${profile.phone}`} style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#dcfce7', border: 'none', borderRadius: 8, padding: '6px 12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <PhoneOutlined /> Call
                  </a>
                  <a href={`https://wa.me/91${profile.phone}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#25d366', border: 'none', borderRadius: 8, padding: '6px 12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <WhatsAppOutlined /> WhatsApp
                  </a>
                </>
              )}
              {!profile.phone && profile.connection_status !== 'accepted' && (
                <span style={{ fontSize: 10, color: '#9ca3af', alignSelf: 'center' }}>📞 जुड़ने पर नंबर दिखेगा</span>
              )}
            </div>

            {/* Basic Info */}
            <div style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>व्यक्तिगत जानकारी</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                {[
                  { label: 'पिता का नाम', value: profile.father_name },
                  { label: 'गोत्र', value: profile.gotra },
                  { label: 'जाति', value: profile.caste },
                  { label: 'कुलदेवी', value: profile.kuldevi },
                  { label: 'जन्मदिन', value: profile.dob_display },
                  { label: 'वैवाहिक', value: profile.marital_status },
                  { label: 'सदस्य बने', value: fmtDate(profile.joining_date) },
                ].map(f => (
                  <div key={f.label}>
                    <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>{f.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{fmt(f.value)}</div>
                  </div>
                ))}
              </div>

              {/* Address */}
              {(profile.village || profile.district) && (
                <div style={{ marginTop: 12, background: '#f9fafb', borderRadius: 8, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <EnvironmentOutlined style={{ color: M, fontSize: 14, flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: '#374151' }}>
                    {[profile.village, profile.tahsil, profile.district, profile.state].filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* Occupation */}
            {profile.occupation?.length > 0 && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>नौकरी / पेशा</div>
                {profile.occupation.map((o, i) => (
                  <div key={i} style={{ background: '#eff6ff', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{o.occ_name || '—'}</div>
                    {o.occ_field && <div style={{ fontSize: 11, color: '#3730a3' }}>{o.occ_field}{o.occ_category ? ` · ${o.occ_category}` : ''}</div>}
                    {(o.occ_district || o.occ_state) && <div style={{ fontSize: 10, color: '#6b7280' }}>{[o.occ_locality, o.occ_district, o.occ_state].filter(Boolean).join(', ')}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Social Status */}
            {profile.social_status?.length > 0 && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <TrophyOutlined style={{ color: '#d97706' }} /> सामाजिक स्थिति
                </div>
                {profile.social_status.map((s, i) => (
                  <div key={i} style={{ background: '#fffbeb', borderRadius: 8, padding: '8px 12px', marginBottom: 6, border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>{s.sn_name || '—'}</div>
                    {s.sn_organisation_name && <div style={{ fontSize: 11, color: '#b45309' }}>{s.sn_organisation_name}</div>}
                    {(s.sn_duration_start || s.sn_duration_end) && (
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                        <CalendarOutlined style={{ marginRight: 3 }} />
                        {[fmtDate(s.sn_duration_start), fmtDate(s.sn_duration_end)].filter(f => f !== '—').join(' – ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Family — only if connected */}
            {profile.family?.length > 0 && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <TeamOutlined style={{ color: M }} /> परिवार
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {profile.family.map((f, i) => (
                    <div key={i} style={{ background: '#fff5f5', border: `1px solid ${M}20`, borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {f.m_photo
                        ? <img src={f.m_photo} alt={f.m_name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                        : <div style={{ width: 28, height: 28, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', color: G, fontSize: 10, fontWeight: 800 }}>{ini(f.m_name)}</div>}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#111' }}>{f.m_name}</div>
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>{f.m_relation_type || f.m_gender || ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Anniversary — only if connected */}
            {profile.anniversary_date && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <HeartOutlined style={{ color: '#db2777', fontSize: 16 }} />
                  <div>
                    <div style={{ fontSize: 10, color: '#9d174d', fontWeight: 600 }}>विवाह वर्षगांठ</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#831843' }}>{fmtDate(profile.anniversary_date)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy notice if not connected */}
            {profile.connection_status !== 'accepted' && (
              <div style={{ margin: '0 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                <UserOutlined style={{ color: '#15803d', flexShrink: 0 }} />
                <div style={{ fontSize: 10, color: '#166534' }}>
                  संपर्क जोड़ने पर मोबाइल नंबर, ईमेल और परिवार की जानकारी दिखेगी।
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
